import React from 'react'
import {
  Box,
  BoxProps,
  Button as ChakraButton,
  ButtonProps,
  shouldForwardProp,
} from '@chakra-ui/react'
import { chakra } from '@chakra-ui/react'
import { ComponentType, cloneElement, forwardRef } from 'react'
import {
  AnimationControls,
  TargetAndTransition,
  VariantLabels,
  Variants,
  isValidMotionProp,
  motion,
} from 'framer-motion'

import { theme } from '../../theme'

export type { ButtonProps }

/**
 * A proxy for a simple Chakra Button component
 *
 * Button is wrapped so that we can ensure we attach a correct
 * Component.name at compile time
 */
export const Button = forwardRef<HTMLButtonElement | null, ButtonProps>(
  (props, ref) => <ChakraButton ref={ref} {...props} />
)

// Extend ButtonProps to include 'href' when using the 'as' prop
type ExtendedButtonProps = ButtonProps & {
  href?: string // Add 'href' as an optional prop
}
/**
 * A button that acts as a link element which can have a href attached
 *
 * Button is wrapped so that we can ensure we attach a correct
 * Component.name at compile time
 */
export const LinkedButton = forwardRef<
  HTMLButtonElement | null,
  ExtendedButtonProps
>(({ href, as: _, ...props }, ref) => (
  <ChakraButton
    as="a"
    href={href}
    ref={ref as React.Ref<HTMLAnchorElement>}
    {...props}
  />
))

const a = (
  <chakra.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 781.26 228.14">
    <path
      fill="currentColor"
      d="m50.8 57.07 23.51 144.49h649.96V61.45L550.91 28.58 50.8 57.07z"
    />
  </chakra.svg>
)

const b = (
  <chakra.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 781.26 228.14">
    <path
      fill="currentColor"
      d="M65.01 54.93v140.01h650.41L733.46 57.8l-81.3-24.17-416.13-7.05L65.01 54.93z"
    />
  </chakra.svg>
)

const c = (
  <chakra.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 781.26 228.14">
    <path
      fill="currentColor"
      d="M719.53 170.44 702.19 35.49 54.14 21.68 33.16 83.06l19.15 92.03 582.45 20.08 84.77-24.73z"
    />
  </chakra.svg>
)

const d = (
  <chakra.svg xmlns="http://www.w3.org/2000/svg" viewBox="60 24.58 671 169">
    <path
      fill="currentColor"
      d="M107.86 24.58 54.17 57.04v117.97l428.09 14.17 219.13-14.17 21.42-75.62-21.42-64.63-593.53-10.18z"
    />
  </chakra.svg>
)

const shapes = {
  a,
  b,
  c,
  d,
}

export const StandoutButton = ({
  children,
  variant = 'primary',
  wrapProps = {},
  ...otherProps
}: Omit<ButtonProps, 'variant'> & {
  variant?: 'tertiary' | 'primary'
  wrapProps?: BoxProps
}) => {
  return (
    <Box
      position="relative"
      display="inline-block"
      {...wrapProps}
      height="100%"
      width="100%"
      alignItems="center"
    >
      <Button width="inherit" variant={variant} {...otherProps}>
        {children}
      </Button>
    </Box>
  )
}

export const RaggedStandoutButton = ({
  children,
  shape = 'c',
  variant = 'primary',
  wrapProps = {},
  ...otherProps
}: Omit<ButtonProps, 'variant'> & {
  shape?: keyof typeof shapes
  variant?: 'tertiary' | 'secondary' | 'primary'
  wrapProps?: BoxProps
}) => {
  return (
    <Box
      position="relative"
      display="flex"
      {...wrapProps}
      height="100%"
      width="100%"
      padding="lg"
    >
      {cloneElement(shapes[shape], {
        color: theme.components.Button.variants[variant].bg,
        position: 'absolute',
        zIndex: -1,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: 'inherit',
        width: '100%',
      })}
      <Button
        // todo: add updated shapes
        borderColor="transparent"
        bg="transparent"
        width="inherit"
        variant={variant}
        {...otherProps}
      >
        {children}
      </Button>
    </Box>
  )
}

export const CtaButtonBase = ({
  children,
  variant = 'secondary',
  ...otherProps
}: Omit<ButtonProps, 'variant'> & {
  variant?: 'secondary' | 'tertiary'
  wrapProps?: BoxProps
}) => {
  return (
    <Button
      variant={variant}
      minW={['fit-content']}
      w={['100%']}
      {...otherProps}
    >
      {children}
    </Button>
  )
}

// Chakra + framer setup:
// https://chakra-ui.com/getting-started/with-framer
const AnimatedChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
})

type WithAnimation = {
  withAnimation?: boolean
  variants?: Variants | undefined
  animate?:
    | boolean
    | AnimationControls
    | TargetAndTransition
    | VariantLabels
    | undefined
  initial?: boolean | VariantLabels | undefined
}

export type WithAnimationWrappedProps<P> = P & WithAnimation

// Let us conviently wrap a component with a motion component and pass the props
export function withAnimation<P>(WrappedComponent: ComponentType<P>) {
  return function AnimationWrapped(props: WithAnimationWrappedProps<P>) {
    if (!props.withAnimation) return <WrappedComponent {...props} />
    return (
      <AnimatedChakraBox
        variants={props.variants}
        animate={props.animate}
        initial={props.initial}
      >
        <WrappedComponent {...props} />
      </AnimatedChakraBox>
    )
  }
}

export const CtaButton = withAnimation(CtaButtonBase)
